import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import api from '@/lib/api';
import Layout from '@/components/Layout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Users, 
  Calendar,
  TrendingUp,
  Blocks,
  Grid3x3
} from 'lucide-react';

// Import analytics sub-components
import { 
  OverviewStats,
  ConsultantsTab,
  MonthlyProductivity
} from '@/components/analytics';
import BlockAnalyticsTab from '@/components/analytics/BlockAnalyticsTab';
import OccupationTab from '@/components/analytics/OccupationTab';
import SecondaryNav from '@/components/SecondaryNav';

function Analytics({ user }) {
  const location = useLocation();
  const queryTab = new URLSearchParams(location.search).get('tab');
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [overview, setOverview] = useState(null);
  const [consultants, setConsultants] = useState([]);
  const [monthlyData, setMonthlyData] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activeTab, setActiveTab] = useState(queryTab || 'overview');

  const isAdmin = user?.role === 'ADMIN';
  const isConsultantOrAdmin = ['ADMIN', 'CONSULTANT'].includes(user?.role?.toUpperCase());

  useEffect(() => {
    fetchData();
  }, []);

  // React to URL query param changes (e.g. nav from Home → ?tab=consultants)
  useEffect(() => {
    if (queryTab) setActiveTab(queryTab);
  }, [queryTab]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch all data in parallel
      const requests = [
        api.get('/projects'),
        api.get('/analytics/overview'),
        api.get('/analytics/consultants'),
        api.get('/notifications'),
        api.get('/task-calendar/events'),
      ];
      
      // Add monthly data request only for admin
      if (isAdmin) {
        requests.push(api.get('/reports/monthly-productivity'));
      }
      
      const responses = await Promise.allSettled(requests);
      
      // Process projects
      if (responses[0].status === 'fulfilled') {
        setProjects(responses[0].value.data);
      }
      
      // Process overview
      if (responses[1].status === 'fulfilled') {
        setOverview(responses[1].value.data);
      }
      
      // Process consultants
      if (responses[2].status === 'fulfilled') {
        setConsultants(responses[2].value.data || []);
      }
      
      // Process notifications
      if (responses[3].status === 'fulfilled') {
        setNotifications(responses[3].value.data || []);
      }
      
      // Process calendar events
      if (responses[4].status === 'fulfilled') {
        const events = responses[4].value.data || [];
        // Transform events for calendar display
        const calEvents = events.map(e => ({
          title: e.title,
          date: e.start,
          type: e.resource?.type || 'deadline',
          project: e.resource?.project_name || ''
        }));
        setCalendarEvents(calEvents);
      }
      
      // Process monthly data (admin only)
      if (isAdmin && responses[5]?.status === 'fulfilled') {
        setMonthlyData(responses[5].value.data);
      }
      
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      toast.error('Erro ao carregar dados analíticos');
    } finally {
      setLoading(false);
    }
  };

  // Calculate derived data
  const activeProjects = projects.filter(p => 
    p.status === 'In Progress' || p.status === 'Em Andamento' || p.status === 'Iniciado' || (p.overall_percentage > 0 && p.overall_percentage < 100)
  );
  
  const avgCompletion = projects.length > 0
    ? projects.reduce((acc, p) => acc + (p.overall_percentage || 0), 0) / projects.length
    : 0;

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700 mx-auto mb-4"></div>
            <p className="text-gray-500">Carregando analytics...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-6">
        {/* Secondary nav (shared with Home) — keeps "Início" reachable */}
        <div className="-mx-6 lg:-mx-8 px-0 -mt-6 lg:-mt-8 bg-white border-b border-gray-200">
          <div className="max-w-[1400px] mx-auto">
            <SecondaryNav user={user} className="border-t-0 pt-2" />
          </div>
        </div>

        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900" data-testid="analytics-title">
              Analytics
            </h1>
            <p className="text-gray-600 mt-1">
              Acompanhe o desempenho dos projetos e equipe
            </p>
          </div>
        </div>

        {/* Tabs Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className={`grid w-full ${isAdmin ? 'grid-cols-5' : 'grid-cols-2'} lg:w-auto lg:inline-flex`}>
            <TabsTrigger value="overview" className="flex items-center gap-2" data-testid="tab-overview">
              <TrendingUp className="w-4 h-4" />
              <span className="hidden sm:inline">Overview</span>
            </TabsTrigger>
            <TabsTrigger value="consultants" className="flex items-center gap-2" data-testid="tab-consultants">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Consultores</span>
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="occupation" className="flex items-center gap-2" data-testid="tab-occupation">
                <Grid3x3 className="w-4 h-4" />
                <span className="hidden sm:inline">Ocupação</span>
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="blocks" className="flex items-center gap-2" data-testid="tab-blocks">
                <Blocks className="w-4 h-4" />
                <span className="hidden sm:inline">Blocos</span>
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="monthly" className="flex items-center gap-2" data-testid="tab-monthly">
                <Calendar className="w-4 h-4" />
                <span className="hidden sm:inline">Mensal</span>
              </TabsTrigger>
            )}
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-6">
            <OverviewStats overview={overview} />
          </TabsContent>

          {/* Consultants Tab */}
          <TabsContent value="consultants" className="mt-6">
            <ConsultantsTab consultants={consultants} />
          </TabsContent>

          {/* Occupation Tab (Admin Only) */}
          {isAdmin && (
            <TabsContent value="occupation" className="mt-6">
              <OccupationTab />
            </TabsContent>
          )}

          {/* Phase Blocks Analytics Tab (Admin Only) */}
          {isAdmin && (
            <TabsContent value="blocks" className="mt-6">
              <BlockAnalyticsTab />
            </TabsContent>
          )}

          {/* Monthly Tab (Admin Only) */}
          {isAdmin && (
            <TabsContent value="monthly" className="mt-6">
              <MonthlyProductivity monthlyData={monthlyData} />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </Layout>
  );
}

export default Analytics;
